/**
 * Portal Switcher Utility
 * Handles seamless portal switching for super admins
 */

import { store } from '../../store';

/**
 * Portal types enumeration
 */
export const PORTAL_TYPES = {
  SUPER_ADMIN: 'super-admin',
  BUREAU: 'bureau',
  ADMIN: 'admin',
  CLIENT: 'client',
  SALES: 'sales',
  EXCO: 'exco',
  DEV: 'dev',
};

/**
 * Portal role mappings
 */
export const PORTAL_ROLES = {
  [PORTAL_TYPES.SUPER_ADMIN]: 'super_admin',
  [PORTAL_TYPES.BUREAU]: 'bureau_agent',
  [PORTAL_TYPES.ADMIN]: 'admin',
  [PORTAL_TYPES.CLIENT]: 'client',
  [PORTAL_TYPES.SALES]: 'sales',
  [PORTAL_TYPES.EXCO]: 'executive',
  [PORTAL_TYPES.DEV]: 'developer',
};

/**
 * Check if user is super admin
 */
export const isSuperAdmin = () => {
  const state = store.getState();
  const { user, maxRoleLevel } = state.auth;

  // Check if user has super admin role (level 3)
  return maxRoleLevel >= 3 || user?.is_superuser || user?.role === 'super_admin';
};

/**
 * Get available portals for user
 */
export const getAvailablePortals = () => {
  const state = store.getState();
  const { roles, maxRoleLevel } = state.auth;

  const availablePortals = [];

  // Super admin has access to all portals
  if (isSuperAdmin()) {
    return Object.values(PORTAL_TYPES);
  }

  // Check role-based access
  if (roles?.includes('bureau_agent')) {
    availablePortals.push(PORTAL_TYPES.BUREAU);
  }
  if (roles?.includes('admin') || maxRoleLevel >= 2) {
    availablePortals.push(PORTAL_TYPES.ADMIN);
  }
  if (roles?.includes('client')) {
    availablePortals.push(PORTAL_TYPES.CLIENT);
  }
  if (roles?.includes('sales')) {
    availablePortals.push(PORTAL_TYPES.SALES);
  }
  if (roles?.includes('executive')) {
    availablePortals.push(PORTAL_TYPES.EXCO);
  }
  if (roles?.includes('developer')) {
    availablePortals.push(PORTAL_TYPES.DEV);
  }

  return availablePortals;
};

/**
 * Switch to a different portal
 * For super admins, this bypasses login and sets appropriate context
 */
export const switchPortal = (targetPortal, navigate) => {
  const state = store.getState();
  const { user, token } = state.auth;

  if (!token || !user) {
    // Not authenticated, redirect to login
    navigate('/login');
    return;
  }

  // Store super admin context if switching from super admin portal
  const currentPath = window.location.pathname;
  const isFromSuperAdmin = currentPath.startsWith('/super-admin');

  if (isFromSuperAdmin && isSuperAdmin()) {
    // Store super admin context for return navigation
    sessionStorage.setItem(
      'superAdminContext',
      JSON.stringify({
        returnPath: currentPath,
        timestamp: Date.now(),
        originalUser: user,
      })
    );

    // Set portal context flag
    sessionStorage.setItem('portalSwitchMode', 'super_admin');
  }

  // Store the target portal for the navigation
  sessionStorage.setItem('targetPortal', targetPortal);

  // Navigate to the target portal
  const portalPaths = {
    [PORTAL_TYPES.BUREAU]: '/bureau',
    [PORTAL_TYPES.ADMIN]: '/admin',
    [PORTAL_TYPES.CLIENT]: '/client',
    [PORTAL_TYPES.SUPER_ADMIN]: '/super-admin',
    [PORTAL_TYPES.SALES]: '/sales',
    [PORTAL_TYPES.EXCO]: '/exco',
    [PORTAL_TYPES.DEV]: '/dev',
  };

  const targetPath = portalPaths[targetPortal] || '/';
  navigate(targetPath);
};

/**
 * Return to super admin portal
 */
export const returnToSuperAdmin = (navigate) => {
  const superAdminContext = sessionStorage.getItem('superAdminContext');

  if (superAdminContext) {
    try {
      const context = JSON.parse(superAdminContext);

      // Clear portal switch mode
      sessionStorage.removeItem('portalSwitchMode');
      sessionStorage.removeItem('targetPortal');

      // Navigate back to super admin portal
      navigate(context.returnPath || '/super-admin');

      // Clear the context after use
      sessionStorage.removeItem('superAdminContext');
    } catch (error) {
      console.error('Error returning to super admin:', error);
      navigate('/super-admin');
    }
  } else {
    navigate('/super-admin');
  }
};

/**
 * Check if currently in portal switch mode
 */
export const isInPortalSwitchMode = () => {
  return sessionStorage.getItem('portalSwitchMode') === 'super_admin';
};

/**
 * Get super admin context
 */
export const getSuperAdminContext = () => {
  const context = sessionStorage.getItem('superAdminContext');
  if (context) {
    try {
      return JSON.parse(context);
    } catch (error) {
      console.error('Error parsing super admin context:', error);
      return null;
    }
  }
  return null;
};

/**
 * Clear portal switch context
 */
export const clearPortalSwitchContext = () => {
  sessionStorage.removeItem('portalSwitchMode');
  sessionStorage.removeItem('targetPortal');
  sessionStorage.removeItem('superAdminContext');
};

const portalSwitcherExport = {
  PORTAL_TYPES,
  PORTAL_ROLES,
  isSuperAdmin,
  getAvailablePortals,
  switchPortal,
  returnToSuperAdmin,
  isInPortalSwitchMode,
  getSuperAdminContext,
  clearPortalSwitchContext,
};

export default portalSwitcherExport;
